import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ProcessExecutionsService, ProcessExecution, JobDefinition, resolverCasillas } from 'src/app/services/process-executions/process-executions.service';

@Component({
  selector: 'app-procesos-masivos',
  templateUrl: './procesos-masivos.component.html',
  styleUrls: ['./procesos-masivos.component.css']
})
export class ProcesosMasivosComponent implements OnInit, OnDestroy {
  jobs: JobDefinition[] = [];
  runs: ProcessExecution[] = [];

  filters = { job_key: '', status: '', project: '', from: '', to: '' };
  datePreset: 'hoy' | 'ayer' | 'semana' | 'mes' | 'all' | 'custom' = 'hoy';
  page = 1;
  limit = 20;
  total = 0;
  loading = false;
  error: string | null = null;
  triggering: string | null = null;
  deleting: string | null = null;
  showCatalog = true;  // catálogo colapsable, default desplegado

  // Refresh automático cada 5s para la lista
  refreshTimer: any = null;

  constructor(
    public processSvc: ProcessExecutionsService,
    private router: Router
  ) {}

  async ngOnInit() {
    await this.loadJobs();
    this.setDatePreset('hoy');  // default: solo runs de hoy
    await this.reload();
    this.refreshTimer = setInterval(() => this.reload(true), 5000);
  }

  setDatePreset(preset: 'hoy' | 'ayer' | 'semana' | 'mes' | 'all' | 'custom') {
    this.datePreset = preset;
    const now = new Date();
    const todayStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    if (preset === 'hoy') {
      this.filters.from = todayStr(now);
      this.filters.to = todayStr(now);
    } else if (preset === 'ayer') {
      const ayer = new Date(now); ayer.setDate(ayer.getDate() - 1);
      this.filters.from = todayStr(ayer);
      this.filters.to = todayStr(ayer);
    } else if (preset === 'semana') {
      const inicioSemana = new Date(now);
      const dow = inicioSemana.getDay() || 7;  // dom=0 → 7
      inicioSemana.setDate(inicioSemana.getDate() - dow + 1);  // lunes
      this.filters.from = todayStr(inicioSemana);
      this.filters.to = todayStr(now);
    } else if (preset === 'mes') {
      const inicioMes = new Date(now.getFullYear(), now.getMonth(), 1);
      this.filters.from = todayStr(inicioMes);
      this.filters.to = todayStr(now);
    } else if (preset === 'all') {
      this.filters.from = '';
      this.filters.to = '';
    }
    if (preset !== 'custom') this.applyFilters();
  }

  ngOnDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  async loadJobs() {
    try {
      const r = await this.processSvc.listJobs();
      this.jobs = r?.jobs || [];
    } catch (e) { console.error(e); }
  }

  async reload(silent = false) {
    if (!silent) this.loading = true;
    this.error = null;
    try {
      const r = await this.processSvc.list({ ...this.filters, page: this.page, limit: this.limit });
      this.runs = r?.items || [];
      this.total = r?.total || 0;
    } catch (e: any) {
      this.error = e?.message || 'Error al cargar';
    } finally {
      this.loading = false;
    }
  }

  applyFilters() { this.page = 1; this.reload(); }

  clearFilters() {
    this.filters = { job_key: '', status: '', project: '', from: '', to: '' };
    this.setDatePreset('hoy');  // siempre volver a "hoy"
  }

  nextPage() {
    if (this.page * this.limit < this.total) { this.page++; this.reload(); }
  }
  prevPage() {
    if (this.page > 1) { this.page--; this.reload(); }
  }

  openDetail(run: ProcessExecution) {
    this.router.navigate(['/console/procesos-masivos', run._id]);
  }

  async triggerJob(job: JobDefinition) {
    if (!confirm(`¿Disparar manualmente "${job.label}"?`)) return;
    this.triggering = job.job_key;
    try {
      const r = await this.processSvc.trigger(job.job_key);
      console.log('trigger ok', r);
      setTimeout(() => this.reload(), 800);
    } catch (e: any) {
      this.error = `Error disparando ${job.job_key}: ${e?.message || ''}`;
    } finally {
      this.triggering = null;
    }
  }

  async deleteRun(run: ProcessExecution) {
    const label = run.job_label || run.job_key;
    const msg = run.status === 'running'
      ? `La corrida "${label}" se solicitó cancelar pero quizás aún esté procesando. ¿Eliminarla igual?\n\nEsto borrará el run, sus items y log de eventos.`
      : `¿Eliminar la corrida "${label}" del ${(new Date(run.started_at || '')).toLocaleString()}?\n\nEsto borrará el run, sus items y log de eventos.`;
    if (!confirm(msg)) return;
    this.deleting = run._id;
    try {
      const r = await this.processSvc.delete(run._id);
      console.log('deleted', r);
      await this.reload(true);
    } catch (e: any) {
      this.error = `Error eliminando: ${e?.error?.msg || e?.message || ''}`;
    } finally {
      this.deleting = null;
    }
  }

  statusBadgeClass(s: string) {
    switch (s) {
      case 'running': return 'bg-blue-100 text-blue-700';
      case 'success': return 'bg-green-100 text-green-700';
      case 'partial': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-200 text-gray-700';
      case 'queued': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  triggerBadge(t: string) {
    switch (t) {
      case 'cron': return '⏰ Cron';
      case 'manual_console': return '👤 Manual';
      case 'manual_api': return '🔧 API';
      case 'change_stream': return '🔄 Stream';
      case 'internal': return '⚙️ Interno';
      default: return t;
    }
  }

  fmtDuration(ms?: number | null) {
    if (ms == null) return '-';
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(1)}s`;
    if (s < 3600) return `${(s / 60).toFixed(1)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  }

  // La cuenta vive en el servicio: la lista y el detalle tienen que dar lo mismo.
  // Antes cada uno tenia su copia y la lista mostraba 97% junto al cartel de "success".
  progressPct(run: ProcessExecution): number {
    return resolverCasillas(run).pct;
  }

  /**
   * El renglon debajo de la barra.
   *
   * NO va como quebrado "ajustadas / revisadas": la mayoria de las corridas ajustan poco
   * o nada sobre un universo grande, y "0 / 22.535" debajo de una barra llena se lee como
   * que fallo — el mismo malentendido que el 97%. Verificado contra staging: c004_update
   * dejo 22.535 sin cambio y ajusto 0, y esta bien que asi sea.
   *
   * Se muestran las dos cifras por separado, cada una con su nombre.
   */
  progressTexto(run: ProcessExecution): string {
    const c = resolverCasillas(run);
    if (c.procesados !== null && c.escaneados !== null) {
      return `${c.escaneados.toLocaleString('es')} revisadas · ${c.procesados.toLocaleString('es')} ajustadas`;
    }
    return `${(run.progress?.processed || 0).toLocaleString('es')} / ${(run.progress?.total || 0).toLocaleString('es')}`;
  }
}
